import { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../context/authContext';

const API = '/api';

// Los roles NO están hardcodeados: se cargan de GET /api/roles (tabla 'roles',
// migración 073). Antes eran este array fijo, y había que mantenerlo en sync con el
// array de utils/roles.js y con el CHECK de la 072: olvidar uno hacía que un rol
// nuevo no se pudiera asignar (400) sin que se entendiera por qué.
//
// El endpoint es solo 'authorize', no 'soloAdmin', porque el select de roles lo
// usan varias pantallas y leer una lista de nombres no expone datos de usuarios.

// Etiquetas de los 3 estados de users.validez (migración 072). 0 y 1 ya existían; el 2 es el
// "dado de baja" que los distingue del "registrado sin validar".
const ESTADOS = [
  { valor: '', etiqueta: 'Todos' },
  { valor: '0', etiqueta: 'Sin validar' },
  { valor: '1', etiqueta: 'Activo' },
  { valor: '2', etiqueta: 'Dado de baja' },
];

// El nombre del rol es la etiqueta. Se accede por 'nombre' y no por 'valor' porque
// es el campo que devuelve el backend, y un mapeo intermedio solo agrega un lugar
// más donde el nombre puede quedar desfasado.
// Etiqueta legible de un rol a partir de su nombre: 'roles' viene de la tabla
// 'roles' y mapea nombre (lo que se guarda) a etiqueta (lo que se ve). Si el rol
// no está en la lista se muestra el nombre crudo, que es mejor que no mostrar nada.
const etiquetaDe = (roles, nombre) => roles.find((r) => r.nombre === nombre)?.etiqueta || nombre;

// Colores del estado, para ver de un vistazo quién puede entrar y quién no.
const CLASE_ESTADO = {
  0: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  1: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  2: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

// Pantalla de admin para gestionar los usuarios registrados.
//
// Lo que hace es cambiar 'users.validez' (alta/baja) y 'users.role'. Las dos
// invariantes de seguridad NO están acá, están en el service
// (usuariosAdminService.js): nadie se desactiva ni se degrada a sí mismo, y no se
// puede dejar el sistema sin admins activos. Esta pantalla muestra el error que
// devuelve el backend, pero un POST directo lo esquivaría igual.
export default function GestionUsuariosPage() {
  const { token, role, userId } = useContext(AuthContext);

  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Roles (tabla 'roles', migración 073). Vienen del backend; acá no hay lista.
  const [roles, setRoles] = useState([]);
  const [errorRoles, setErrorRoles] = useState('');
  const [mensajeRoles, setMensajeRoles] = useState('');

  // Alta de rol. El nombre es el valor que se guarda en users.role, así que va
  // separado de la etiqueta: el nombre es técnico y no se puede cambiar después
  // (los usuarios ya lo tienen guardado), la etiqueta sí.
  const [nombreRolNuevo, setNombreRolNuevo] = useState('');
  const [etiquetaRolNuevo, setEtiquetaRolNuevo] = useState('');
  const [creandoRol, setCreandoRol] = useState(false);

  const [filtroRol, setFiltroRol] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [buscarTexto, setBuscarTexto] = useState('');

  // Id del usuario que se está editando, para disablear los controles mientras
  // corre el request y evitar el doble click (dos bajas del mismo usuario).
  const [ocupado, setOcupado] = useState(null);

  // Edición multi-rol de una fila (migración 074). Al tocar "Editar" se copia la
  // lista de roles del usuario acá, se edita contra estos estados y al guardar se
  // manda la lista COMPLETA con PATCH /usuarios/:id/roles. Nunca se manda un rol
  // por vez: el backend reemplaza la lista entera, y un PUT incompleto dejaría
  // los otros roles afuera sin que el admin se entere.
  const [editandoId, setEditandoId] = useState(null);
  const [rolesEnEdicion, setRolesEnEdicion] = useState([]);
  const [principalEnEdicion, setPrincipalEnEdicion] = useState(null);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {}),
    [token]
  );

  const cargar = async () => {
    setCargando(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filtroRol) params.set('role', filtroRol);
      if (filtroEstado !== '') params.set('estado', filtroEstado);
      if (buscarTexto.trim()) params.set('search', buscarTexto.trim());

      const res = await fetch(`${API}/usuarios/gestion?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Error al cargar los usuarios');
      const data = await res.json();
      setUsuarios(data.usuarios || []);
    } catch (e) {
      setError(e.message);
    }
    setCargando(false);
  };

  useEffect(() => {
    if (role !== 'admin') return;
    cargar();
  }, [role, filtroRol, filtroEstado, buscarTexto]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buscar es explícito (botón / Enter), no por tecla: cada pulsación dispararía
  // un request y la tabla parpadearía mientras se escribe.
  const buscar = () => setBuscarTexto(busqueda);
  const limpiarFiltros = () => {
    setFiltroRol('');
    setFiltroEstado('');
    setBusqueda('');
    setBuscarTexto('');
  };

  // ------------------------------------------------------------- roles

  // 'incluirInactivos=1': la pantalla tiene que mostrar también los roles dados de
  // baja, por el mismo motivo que las obras dadas de baja aparecen en el listado:
  // si un usuario tiene un rol que ya no está en el select, el admin tiene que
  // poder verlo, y entender por qué no lo puede cambiar.
  const cargarRoles = async () => {
    try {
      const res = await fetch(`${API}/roles?incluirInactivos=1`, { headers });
      if (!res.ok) throw new Error('Error al cargar los roles');
      const data = await res.json();
      setRoles(data.roles || []);
      setErrorRoles('');
    } catch (e) {
      // No se pisa el error de la tabla de usuarios: son dos problemas distintos
      // y un error de carga de roles no debería tapar un "usuario no existe".
      setErrorRoles(e.message);
    }
  };

  useEffect(() => {
    if (role !== 'admin') return;
    cargarRoles();
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const crearRol = async (e) => {
    e?.preventDefault();
    const nombre = nombreRolNuevo.trim();
    if (!nombre) {
      setErrorRoles('Falta el nombre del rol');
      return;
    }

    setCreandoRol(true);
    setErrorRoles('');
    setMensajeRoles('');
    try {
      const res = await fetch(`${API}/roles`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          nombre,
          // Si no se carga etiqueta, el backend usa el nombre. Se manda vacío
          // (y no undefined) para que la clave no quede en el JSON.
          etiqueta: etiquetaRolNuevo.trim() || nombre,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // El 409 (ya existe) y el 400 (formato) llegan con el motivo desde el
        // service, que es quien tiene el detalle.
        setErrorRoles(data.message || 'No se pudo crear el rol');
        return;
      }

      setMensajeRoles(`Rol '${data.rol.nombre}' creado.`);
      setNombreRolNuevo('');
      setEtiquetaRolNuevo('');
      await cargarRoles();
      // El select de rol de la tabla se arma con 'roles', así que sin recargar
      // acá el rol nuevo no aparecería hasta que se recargue la pantalla.
    } catch (err) {
      setErrorRoles(err.message);
    } finally {
      setCreandoRol(false);
    }
  };

  const accion = async (id, campo, valor, textoOk) => {
    setOcupado(`${id}-${campo}`);
    setError('');
    setMensaje('');
    try {
      const res = await fetch(`${API}/usuarios/${id}/${campo}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(campo === 'validez' ? { validez: valor } : { role: valor }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Los guards del service llegan acá como 400 con el motivo. Se muestran
        // tal cual: son la explicación de por qué la acción no se hizo.
        setError(data.message || 'No se pudo completar la operación');
        return;
      }

      // El backend devuelve un 'aviso' cuando el efecto no es inmediato. Hay que
      // mostrarlo: el admin cambió el rol y el usuario sigue viendo lo viejo hasta
      // que su token expire, y sin el aviso va a pensar que no se guardó.
      setMensaje(data.aviso ? `${textoOk} ${data.aviso}` : textoOk);
      await cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setOcupado(null);
    }
  };

  // ------------------------------------------------- edición multi-rol (074)
  //
  // Un solo PATCH manda la lista COMPLETA (PATCH /usuarios/:id/roles). Nunca un rol
  // por vez: el backend reemplaza la lista entera, y mandar solo lo que cambió
  // dejaría los otros roles afuera sin que el admin se entere. Es la misma trampa
  // que el PUT /ongs/:id/contactos con el array pelado.
  const editarRoles = (u) => {
    setEditandoId(u.id);
    setRolesEnEdicion([...(u.roles || [])]);
    setPrincipalEnEdicion(u.role || null);
    setError('');
    setMensaje('');
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setRolesEnEdicion([]);
    setPrincipalEnEdicion(null);
  };

  const alternarRol = (nombre, activo) => {
    setRolesEnEdicion((prev) => (activo ? [...prev, nombre] : prev.filter((r) => r !== nombre)));
  };

  const guardarRoles = async (u) => {
    // Lista vacía permitida: significa "no recibe avisos" y deja users.role en NULL.
    // Si se sacó el principal, se manda el primero de la lista restante; si no queda
    // ninguno, se manda null y el backend deja el principal en NULL.
    const lista = rolesEnEdicion;
    const principal = lista.includes(principalEnEdicion) ? principalEnEdicion : (lista[0] || null);

    setOcupado(`${u.id}-roles`);
    setError('');
    setMensaje('');
    try {
      const res = await fetch(`${API}/usuarios/${u.id}/roles`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ roles: lista, principal }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Los guards del service llegan acá como 400 con el motivo: el rol no
        // existe, es el último admin, o no te cambiás los roles a vos mismo.
        setError(data.message || 'No se pudieron actualizar los roles');
        return;
      }

      setMensaje(data.aviso ? `Roles actualizados. ${data.aviso}` : 'Roles actualizados.');
      cancelarEdicion();
      await cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setOcupado(null);
    }
  };

  if (role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        Acceso denegado. Solo administradores.
      </div>
    );
  }

  const resumen = usuarios.reduce(
    (acc, u) => { acc[u.validez] = (acc[u.validez] || 0) + 1; return acc; },
    {}
  );

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-bold mb-1">Gestión de usuarios</h1>
      <p className="text-sm text-gray-500 mb-4">
        Alta y baja de cuentas, y asignación de roles. Solo vos (admin) podés entrar a esta pantalla.
      </p>

      {/* Resumen de lo que hay listado con los filtros actuales */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Sin validar</div>
          <div className="text-2xl font-bold text-amber-600">{resumen[0] || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Activos</div>
          <div className="text-2xl font-bold text-green-600">{resumen[1] || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg p-3">
          <div className="text-xs text-gray-500">Dados de baja</div>
          <div className="text-2xl font-bold text-red-600">{resumen[2] || 0}</div>
        </div>
      </div>

      {/* Alta de roles. Va arriba de los filtros porque los filtros y los selects de la
          tabla se arman con 'roles': crear uno acá los actualiza sin recargar la
          pantalla. */}
      <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
        <summary className="text-sm font-medium cursor-pointer select-none">
          Agregar un rol
        </summary>

        <p className="text-xs text-gray-500 mt-2 mb-2">
          El <span className="font-mono">nombre</span> es el valor que se guarda en cada
          usuario: minúsculas, números y guion bajo, sin espacios (ej: <span className="font-mono">pagos</span>,{' '}
          <span className="font-mono">obra_social</span>). Después no se puede cambiar.
          La etiqueta es solo lo que se ve en pantalla y se puede elegir libremente.
          Un rol nuevo es únicamente una etiqueta de agrupación: no habilita
          notificaciones ni permisos.
        </p>

        <form onSubmit={crearRol} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Nombre</label>
            <input
              value={nombreRolNuevo}
              onChange={(e) => setNombreRolNuevo(e.target.value)}
              placeholder="pagos"
              disabled={creandoRol}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-40 font-mono disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Etiqueta (opcional)</label>
            <input
              value={etiquetaRolNuevo}
              onChange={(e) => setEtiquetaRolNuevo(e.target.value)}
              placeholder="Área de Pagos"
              disabled={creandoRol}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-48 disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={creandoRol}
            className="border border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-300 rounded px-3 py-1 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creandoRol ? 'Creando…' : 'Crear rol'}
          </button>
        </form>

        {/* Los errores y el aviso de los roles van acá adentro y no junto al mensaje de
          la tabla de usuarios: son dos operações distintas y mezcladas el admin
          no sabe cuál falló. */}
        {errorRoles && <div className="mt-2 text-sm text-red-600">{errorRoles}</div>}
        {mensajeRoles && <div className="mt-2 text-sm text-green-600">{mensajeRoles}</div>}
      </details>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rol</label>
          <select
            value={filtroRol}
            onChange={(e) => setFiltroRol(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">Todos</option>
            {roles.map((r) => <option key={r.nombre} value={r.nombre}>{r.etiqueta}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          >
            {ESTADOS.map((s) => <option key={s.valor} value={s.valor}>{s.etiqueta}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Buscar</label>
          <div className="flex gap-2">
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
              placeholder="nombre, email o usuario"
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-48"
            />
            <button
              onClick={buscar}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Buscar
            </button>
          </div>
        </div>

        <button onClick={limpiarFiltros} className="text-sm text-blue-600 hover:text-blue-800 underline pb-1">
          Limpiar filtros
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
      {mensaje && <div className="mb-3 text-sm text-green-600">{mensaje}</div>}

      {/* Tabla */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 text-left">
            <tr>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Roles</th>
              <th className="px-3 py-2">Último acceso</th>
              <th className="px-3 py-2 text-center">Sesiones</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!cargando && usuarios.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Sin usuarios para estos filtros</td></tr>
            )}
            {!cargando && usuarios.map((u) => {
              const esYo = Number(u.id) === Number(userId);
              const ocupadoValidez = ocupado === `${u.id}-validez`;
              const ocupadoRoles = ocupado === `${u.id}-roles`;

              // Opciones del editor de la fila. Se agregan los roles que el usuario
              // tenga y no vinieran en GET /roles: no debería pasar (073 backfillea
              // la tabla), pero si pasara el checkbox no aparecería y al guardar se
              // le sacaría el rol sin querer.
              const nombresEnTabla = roles.map((r) => r.nombre);
              const opcionesRol = [
                ...roles,
                ...(u.roles || []).filter((r) => !nombresEnTabla.includes(r)),
              ];

              return (
                <tr key={u.id} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2">
                    {u.name}
                    {esYo && <span className="ml-2 text-xs text-gray-500">(vos)</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${CLASE_ESTADO[u.validez] || ''}`}>
                      {u.estado}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {editandoId === u.id ? (
                      <div className="min-w-[240px] space-y-2">
                        {/* Lista completa en un solo PATCH. Un rol por vez daría de
                            baja el resto de los roles del usuario. */}
                        <div className="space-y-1">
                          {opcionesRol.map((r) => {
                            const tiene = rolesEnEdicion.includes(r.nombre);
                            return (
                              <label
                                key={r.nombre}
                                className={`flex items-center gap-2 text-sm ${r.activo ? '' : 'text-gray-400'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={tiene}
                                  // Un rol dado de baja se puede sacar (es la única
                                  // forma), pero no agregar: el backend lo deja pasar
                                  // solo si ya lo tenía.
                                  disabled={(!r.activo && !tiene) || ocupadoRoles}
                                  onChange={(e) => alternarRol(r.nombre, e.target.checked)}
                                />
                                <span>{etiquetaDe(roles, r.nombre)}</span>
                                {!r.activo && <span className="text-[10px] text-amber-600">(dado de baja)</span>}
                              </label>
                            );
                          })}
                          {opcionesRol.length === 0 && (
                            <div className="text-xs text-gray-400">No hay roles cargados</div>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Rol principal (los permisos salen de acá)
                          </label>
                          <select
                            value={rolesEnEdicion.includes(principalEnEdicion) ? principalEnEdicion : ''}
                            onChange={(e) => setPrincipalEnEdicion(e.target.value || null)}
                            disabled={ocupadoRoles}
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-full disabled:opacity-50"
                          >
                            <option value="">(sin rol principal)</option>
                            {rolesEnEdicion.map((rn) => (
                              <option key={rn} value={rn}>{etiquetaDe(roles, rn)}</option>
                            ))}
                          </select>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => guardarRoles(u)}
                            disabled={ocupadoRoles}
                            className="border border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-300 rounded px-2 py-1 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                          >
                            {ocupadoRoles ? '…' : 'Guardar'}
                          </button>
                          <button
                            onClick={cancelarEdicion}
                            disabled={ocupadoRoles}
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1">
                        {(u.roles || []).map((rn) => (
                          <span
                            key={rn}
                            // El principal va con borde y negrita: es el que define los
                            // permisos, y ver solo la lista hace que no se note cuál es.
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              rn === u.role
                                ? 'bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                            title={rn === u.role ? 'Rol principal (permisos)' : 'Rol adicional'}
                          >
                            {etiquetaDe(roles, rn)}
                            {rn === u.role && ' ★'}
                          </span>
                        ))}
                        {(u.roles || []).length === 0 && (
                          <span className="text-xs text-gray-400">(sin roles)</span>
                        )}
                        <button
                          onClick={() => editarRoles(u)}
                          disabled={esYo || ocupadoRoles}
                          title={esYo ? 'No podés cambiarte los roles a vos mismo' : 'Editar roles'}
                          className="text-xs text-blue-600 hover:text-blue-800 underline ml-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Editar
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                    {u.last_login ? new Date(u.last_login).toLocaleString('es-AR') : 'nunca'}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-300">{u.sesiones_vivas}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {u.validez === 1 ? (
                      <button
                        onClick={() => accion(u.id, 'validez', 2, 'Usuario dado de baja.')}
                        disabled={esYo || ocupadoValidez}
                        title={esYo ? 'No podés darte de baja a vos mismo' : ''}
                        className="border border-red-300 text-red-700 dark:border-red-600 dark:text-red-300 rounded px-2 py-1 text-xs hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ocupadoValidez ? '…' : 'Dar de baja'}
                      </button>
                    ) : (
                      <button
                        onClick={() => accion(u.id, 'validez', 1, 'Usuario dado de alta.')}
                        disabled={ocupadoValidez}
                        className="border border-green-300 text-green-700 dark:border-green-600 dark:text-green-300 rounded px-2 py-1 text-xs hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ocupadoValidez ? '…' : 'Dar de alta'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        Dar de baja corta las sesiones abiertas del usuario. No podés darte de baja a vos mismo ni
        dejar al sistema sin ningún administrador activo.
      </p>
    </div>
  );
}